import React from 'react';
import { Tag, Space, Button, Dropdown, Menu, Typography, Tooltip } from 'antd';
import {
  FilterOutlined,
  CloseOutlined,
  DownOutlined,
  SaveOutlined,
  DeleteOutlined,
  FireOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { ExtendedSearchOrdersParams, FilterPreset, QuickFilterType } from '../types';
import { applyQuickFilter, getDefaultFilterPresets, formatDate } from '../utils/orderUtils';

const { Text } = Typography;

export interface OrderFiltersProps {
  /** 当前搜索参数 */
  params: ExtendedSearchOrdersParams;
  /** 筛选条件变化回调 */
  onFilterChange: (params: Partial<ExtendedSearchOrdersParams>) => void;
  /** 清除所有筛选回调 */
  onClearAll?: () => void;
  /** 清除单个筛选回调 */
  onClearFilter?: (filterKey: keyof ExtendedSearchOrdersParams) => void;
  /** 是否显示快速筛选 */
  showQuickFilters?: boolean;
  /** 是否显示筛选预设 */
  showPresets?: boolean;
  /** 是否显示活动筛选标签 */
  showActiveFilters?: boolean;
  /** 自定义筛选预设 */
  customPresets?: FilterPreset[];
  /** 是否正在加载 */
  loading?: boolean;
  /** 是否禁用 */
  disabled?: boolean;
  /** 用户角色，用于过滤预设选项 */
  userRole?: string;
}

const OrderFilters: React.FC<OrderFiltersProps> = ({
  params,
  onFilterChange,
  onClearAll,
  onClearFilter,
  showQuickFilters = true,
  showPresets = true,
  showActiveFilters = true,
  customPresets,
  loading = false,
  disabled = false,
  userRole = 'user'
}) => {
  const { t } = useTranslation(['modules', 'common']);

  // 获取默认筛选预设
  const defaultPresets = getDefaultFilterPresets(params.company_id, userRole, t);
  const presets = customPresets || defaultPresets;

  // 快速筛选选项（仅包含后端支持的筛选类型）
  const quickFilters: Array<{
    key: QuickFilterType;
    label: string;
    icon: React.ReactNode;
    color: string;
    description: string;
  }> = [
    {
      key: 'today',
      label: t('modules:order.query.filters.today', '今日订单'),
      icon: <FireOutlined />,
      color: 'red',
      description: t('modules:order.query.filters.todayDesc', '今日创建的订单')
    },
    {
      key: 'pending',
      label: t('modules:order.query.filters.pending', '待处理订单'),
      icon: <ClockCircleOutlined />,
      color: 'orange',
      description: t('modules:order.query.filters.pendingDesc', '需要处理的订单')
    },
    {
      key: 'processing',
      label: t('modules:order.query.filters.processing', '处理中订单'),
      icon: <ClockCircleOutlined />,
      color: 'blue',
      description: t('modules:order.query.filters.processingDesc', '正在处理的订单')
    },
    {
      key: 'cancelled',
      label: t('modules:order.query.filters.cancelled', '已取消订单'),
      icon: <CloseCircleOutlined />,
      color: 'gray',
      description: t('modules:order.query.filters.cancelledDesc', '已取消的订单')
    },
    {
      key: 'delivered',
      label: t('modules:order.query.filters.delivered', '已送达订单'),
      icon: <CheckCircleOutlined />,
      color: 'green',
      description: t('modules:order.query.filters.deliveredDesc', '已送达的订单')
    },
    {
      key: 'recent',
      label: t('modules:order.query.filters.recent', '最近订单'),
      icon: <ClockCircleOutlined />,
      color: 'purple',
      description: t('modules:order.query.filters.recentDesc', '7天内的订单')
    }
  ];

  // 应用快速筛选
  const handleQuickFilter = (filterType: QuickFilterType) => {
    const newParams = applyQuickFilter(filterType, params);
    onFilterChange(newParams);
  };

  // 应用筛选预设
  const handlePresetSelect = (preset: FilterPreset) => {
    onFilterChange(preset.params);
  };

  // 获取活动筛选条件
  const getActiveFilters = () => {
    const activeFilters: Array<{
      key: keyof ExtendedSearchOrdersParams;
      label: string;
      value: any;
      displayValue: string;
    }> = [];

    const filterLabels: Record<string, string> = {
      company_id: t('modules:order.query.filters.companyId', '公司ID'),
      customer_id: t('modules:order.query.filters.customerId', '客户ID'),
      status: t('modules:order.query.filters.status', '状态'),
      start_date: t('modules:order.query.filters.startDate', '开始日期'),
      end_date: t('modules:order.query.filters.endDate', '结束日期'),
      // 后端API仅支持以上字段
      limit: t('modules:order.query.filters.limit', '每页数量'),
      offset: t('modules:order.query.filters.offset', '偏移量'),
      sort_by: t('modules:order.query.filters.sortBy', '排序字段'),
      sort_order: t('modules:order.query.filters.sortOrder', '排序方向')
    };

    // 状态显示文本映射
    const statusTexts: Record<string, string> = {
      pending: t('modules:order.query.filters.statusPending', '待处理'),
      processing: t('modules:order.query.filters.statusProcessing', '处理中'),
      shipped: t('modules:order.query.filters.statusShipped', '已发货'),
      delivered: t('modules:order.query.filters.statusDelivered', '已送达'),
      cancelled: t('modules:order.query.filters.statusCancelled', '已取消')
    };

    Object.entries(params).forEach(([key, value]) => {
      // 排除非业务筛选字段：公司ID、分页参数、排序参数
      const excludedKeys = ['company_id', 'limit', 'offset', 'sort_by', 'sort_order'];
      if (value !== undefined && value !== null && value !== '' && !excludedKeys.includes(key)) {
        const filterKey = key as keyof ExtendedSearchOrdersParams;
        const label = filterLabels[key] || key;

        // 格式化显示值
        let displayValue = value;
        if (filterKey === 'status') {
          displayValue = statusTexts[value as string] || value;
        } else if (filterKey === 'start_date' || filterKey === 'end_date') {
          displayValue = formatDate(value as string, 'short');
        }

        activeFilters.push({
          key: filterKey,
          label,
          value,
          displayValue: String(displayValue)
        });
      }
    });

    return activeFilters;
  };

  // 清除单个筛选条件
  const handleClearFilter = (filterKey: keyof ExtendedSearchOrdersParams) => {
    if (onClearFilter) {
      onClearFilter(filterKey);
    } else {
      const newParams = { ...params };
      delete newParams[filterKey];
      onFilterChange(newParams);
    }
  };

  // 清除所有筛选条件
  const handleClearAll = () => {
    if (onClearAll) {
      onClearAll();
    } else {
      onFilterChange({
        company_id: params.company_id,
        limit: params.limit || 20,
        offset: 0
      });
    }
  };

  // 检查是否有活动筛选条件
  const activeFilters = getActiveFilters();
  const hasActiveFilters = activeFilters.length > 0;

  // 筛选预设菜单
  const presetMenu = () => (
    <Menu>
      <Menu.ItemGroup title={t('modules:order.query.filters.presetGroup', '筛选预设')}>
        {presets.map(preset => (
          <Menu.Item
            key={preset.id}
            onClick={() => handlePresetSelect(preset)}
            icon={preset.isDefault ? <FilterOutlined /> : undefined}
          >
            <div>
              <div>{preset.name}</div>
              {preset.description && (
                <div style={{ fontSize: '12px', color: '#999' }}>
                  {preset.description}
                </div>
              )}
            </div>
          </Menu.Item>
        ))}
      </Menu.ItemGroup>
      <Menu.Divider />
      <Menu.Item
        key="save-preset"
        icon={<SaveOutlined />}
        disabled={disabled}
      >
        {t('modules:order.query.filters.savePreset', '保存当前筛选为预设')}
      </Menu.Item>
      <Menu.Item
        key="manage-presets"
        icon={<DeleteOutlined />}
        disabled={disabled}
      >
        {t('modules:order.query.filters.managePresets', '管理筛选预设')}
      </Menu.Item>
    </Menu>
  );

  return (
    <div className="order-filters" style={{ marginBottom: 8 }}>
      {/* 快速筛选和筛选预设 */}
      {(showQuickFilters || showPresets) && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap', overflowX: 'auto', paddingBottom: 4 }}>
            {showQuickFilters && (
              <>
                <Text type="secondary" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {t('modules:order.query.filters.quickFilters', '快速筛选')}:
                </Text>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  {quickFilters.map(filter => (
                    <Tooltip key={filter.key} title={filter.description}>
                      <Tag
                        icon={filter.icon}
                        color={filter.color}
                        style={{ cursor: 'pointer', flexShrink: 0, margin: 0 }}
                        onClick={() => !disabled && handleQuickFilter(filter.key)}
                      >
                        {filter.label}
                      </Tag>
                    </Tooltip>
                  ))}
                </div>
              </>
            )}

            {showPresets && (
              <div style={{ flexShrink: 0, marginLeft: 'auto' }}>
                <Dropdown popupRender={presetMenu} trigger={['click']} disabled={disabled}>
                  <Button size="small" icon={<FilterOutlined />}>
                    {t('modules:order.query.filters.presets', '筛选预设')}
                    <DownOutlined />
                  </Button>
                </Dropdown>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 活动筛选条件 */}
      {showActiveFilters && hasActiveFilters && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap', overflowX: 'auto', paddingBottom: 4 }}>
            <Text type="secondary" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
              {t('modules:order.query.filters.activeFilters', '当前筛选')}:
            </Text>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              {activeFilters.map(filter => (
                <Tag
                  key={String(filter.key)}
                  closable={!disabled}
                  onClose={() => handleClearFilter(filter.key)}
                  style={{ margin: 0, flexShrink: 0 }}
                >
                  {filter.label}: {filter.displayValue}
                </Tag>
              ))}
            </div>
            <Button
              type="link"
              size="small"
              icon={<CloseOutlined />}
              onClick={handleClearAll}
              disabled={disabled}
              style={{ flexShrink: 0 }}
            >
              {t('modules:order.query.filters.clearAll', '清除全部')}
            </Button>
          </div>
        </div>
      )}

      {/* 筛选统计信息 */}
      {hasActiveFilters && (
        <div style={{ marginBottom: 8 }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {t('modules:order.query.filters.activeCount', '当前应用了')} {activeFilters.length} {t('modules:order.query.filters.filters', '个筛选条件')}
          </Text>
        </div>
      )}
    </div>
  );
};

export default OrderFilters;